import React from 'react';
import './Expedition.css';

// ─── Expédition du transitaire (SIFA) ─────────────────────────────────────────
// Troisième source du suivi des pièces, et la seule qui parle du TRANSPORT :
//   📘 DATACAR    ce que le DMS déclare (la commande)
//   📦 BL MAGASIN le fait constaté à l'entrée en stock
//   🚢 SIFA       le trajet — mode, compagnie, arrivée réelle, dédouanement
//
// Règle de lecture, jamais transgressée à l'écran : ETD et ETA sont des
// PRÉVISIONS, RTA est un FAIT. Et `date_livraison` est la clôture du dossier
// SIFA, pas la réception au magasin (elle tombe 2 à 4 jours plus tôt) : elle
// n'est donc jamais présentée comme une réception.

const MODES = {
  AER: { icone: '✈', libelle: 'Aérien' },
  MAR: { icone: '🚢', libelle: 'Maritime' },
  MIX: { icone: '✈🚢', libelle: 'Aérien et maritime' },
};

export function jourCourt(iso) {
  const s = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}`;
}

// Le jalon le plus avancé qu'on connaisse, avec SON VERBE. Une date nue dans
// une cellule ne veut rien dire — « ✈ 02/08 » n'apprend rien à personne. Le
// trajet a quatre étapes, on affiche celle où la marchandise se trouve :
//   expédié → arrivé (sur l'île) → dédouané → [réception magasin = BL Datacar]
// La règle d'or tient dans le style : ETD et ETA sont des PRÉVISIONS (en
// italique, mot « Prévu »), RTA et le dédouanement sont des FAITS.
export function jalon(sifa) {
  if (!sifa) return null;
  if (sifa.date_dedouanement) return { mot: 'Dédouané', date: sifa.date_dedouanement, prevision: false };
  if (sifa.date_rta) return { mot: 'Arrivé', date: sifa.date_rta, prevision: false };
  if (sifa.date_eta) return { mot: 'Prévu', date: sifa.date_eta, prevision: true };
  if (sifa.date_etd) return { mot: 'Expédié', date: sifa.date_etd, prevision: false };
  return null;
}

function detail(sifa) {
  const l = [];
  const mode = MODES[sifa.transport];
  l.push(`${mode ? mode.libelle : 'Transport'}${sifa.compagnie ? ` · ${sifa.compagnie}` : ''}`);
  if (sifa.entite) l.push(sifa.entite);
  if (sifa.no_conteneur) l.push(`Conteneur ${sifa.no_conteneur}`);
  else if (sifa.ref_expedition) l.push(`LTA ${sifa.ref_expedition}`);
  if (sifa.date_etd || sifa.date_eta) {
    l.push(`Prévu : départ ${jourCourt(sifa.date_etd) || '—'}, arrivée ${jourCourt(sifa.date_eta) || '—'}`);
  }
  if (sifa.date_rta) l.push(`Arrivé sur l'île le ${jourCourt(sifa.date_rta)} (constaté)`);
  if (sifa.date_dedouanement) l.push(`Dédouané le ${jourCourt(sifa.date_dedouanement)}`);
  l.push('La réception au magasin, elle, est le bon de livraison Datacar.');
  if (sifa.nb_expeditions > 1) l.push(`${sifa.nb_expeditions} expéditions pour cette commande`);
  if (sifa.litige) l.push('⚠️ Litige ouvert sur l\'expédition');
  if (sifa.arrivee_en_retard) l.push('⚠️ Arrivée en retard');
  return l.join('\n');
}

/**
 * Pastille d'expédition. `sifa` vient de l'entrepôt A97 DATA (lecture seule) :
 * absent = cette commande n'est pas passée par le transitaire, et on n'affiche
 * alors rien du tout — les trois quarts du carnet sont dans ce cas, un tiret
 * partout ne serait que du bruit.
 */
export function ExpeditionChip({ sifa, livree = false }) {
  if (!sifa) return null;
  const mode = MODES[sifa.transport] || { icone: '📦', libelle: 'Transport' };
  // La fiche GARDE l'historique — c'est là qu'on retrouve la LTA ou le
  // conteneur pour ouvrir une réclamation. Mais elle cesse de crier : un
  // retard sur une pièce déjà reçue n'est plus une alerte, seul le litige
  // continue d'en être une.
  const alerte = sifa.litige || (sifa.arrivee_en_retard && !livree);
  const j = jalon(sifa);
  return (
    <span className={`exp-chip${alerte ? ' exp-chip--alerte' : ''}${livree && !alerte ? ' exp-chip--histoire' : ''}`} title={detail(sifa)}>
      <span className="exp-mode">{mode.icone}</span>
      {j && (
        <span className={`exp-jalon${j.prevision ? ' exp-jalon--prevu' : ''}`}>
          {j.mot} <span className="exp-date">{jourCourt(j.date)}</span>
        </span>
      )}
      {alerte && <span className="exp-alerte">⚠</span>}
    </span>
  );
}

/**
 * Marqueur d'OR dans le tableau principal : un SIGNAL, pas une donnée.
 *
 * Deux itérations pour trouver la bonne dose (2026-09-01) : la pastille
 * complète « ✈ Arrivé 02/08 » a été jugée inutile à ce niveau — au niveau de
 * l'OR, une date unique est trompeuse (cet OR-là mêle 5 expéditions) et le
 * statut de la commande suffit. Mais sans rien du tout, on ne sait plus quelles
 * lignes valent qu'on les ouvre. D'où ce compromis : le seul mode de transport,
 * en un glyphe, qui dit « il y a du suivi transitaire ici » — le détail se lit
 * ligne par ligne dans la modale. Volontairement SANS cadre : ce n'est pas un
 * troisième statut à côté de Statut et Facturation.
 */
export function ExpeditionMarqueur({ resume }) {
  if (!resume) return null;
  // Silence quand tout est arrivé et que rien n'est en litige (2026-09-01) :
  // mesuré sur la base réelle, 3 487 des 4 879 OR marqués étaient dans ce cas —
  // 71 % de pictos qui n'appelaient aucun geste. « Arrivée en retard » sur une
  // pièce déjà en stock est de l'histoire : elle intéresse les rapports, pas
  // l'atelier.
  if (!resume.en_transit && !resume.litige) return null;
  const mode = MODES[resume.transport] || { icone: '📦', libelle: 'Transport' };
  const alerte = resume.litige || (resume.arrivee_en_retard && resume.en_transit);
  const titre = [
    `${resume.nb_lignes} ligne(s) suivie(s) par le transitaire — ${mode.libelle}`,
    resume.derniere_arrivee ? `Dernière arrivée sur l'île : ${jourCourt(resume.derniere_arrivee)}` : null,
    resume.litige ? '⚠️ Litige ouvert sur une expédition' : null,
    resume.arrivee_en_retard ? '⚠️ Une expédition est arrivée en retard' : null,
    'Le détail est dans la fiche, ligne par ligne.',
  ].filter(Boolean).join('\n');
  return (
    <span className={`exp-marqueur${alerte ? ' exp-marqueur--alerte' : ''}`} title={titre} aria-label={titre}>
      {mode.icone}{alerte && <span className="exp-alerte">⚠</span>}
    </span>
  );
}
